import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { ConfirmationDialogComponent, JsonEditorComponent } from '@shared/ui';
import { StepAction, StepTask } from '@telespot/domain';
import { Observable, of, Subject } from 'rxjs';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';

import { AlgorithmSwitcherComponent } from '../../algorithms/switch/algorithm-switcher.component';
import {
  CategoryListItem,
  CategoryManagementListComponent,
} from '../category-management-list/category-management-list.component';
import {
  CloudSubject,
  CloudSubjectSelectorComponent,
} from '../cloud-subject-selector/cloud-subject-selector.component';
import {
  GenerateFinding,
  GenerateFindingSelectorComponent,
} from '../generate-finding-selector/generate-finding-selector.component';
import { PipelineCategoryEditorDialogComponent } from '../pipeline-category-editor/pipeline-category-editor-dialog.component';
import { PipelineCategoryResult } from '../../../state/pipeline-category.state';
import { EDITOR_DIALOG_MEDIUM } from '../../editor-dialog-sizes';

import { PipelineStepEditorFacade } from './pipeline-step-editor.facade';

@Component({
  selector: 'ts-pipeline-step-editor',
  templateUrl: './pipeline-step-editor.component.html',
  styleUrls: ['./pipeline-step-editor.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatIconModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    AlgorithmSwitcherComponent,
    CategoryManagementListComponent,
    CloudSubjectSelectorComponent,
    GenerateFindingSelectorComponent,
    JsonEditorComponent,
  ],
})
export class PipelineStepEditorComponent implements OnInit, OnDestroy {
  public readonly categoryItems$ = this.facade.categoryItems$;
  public readonly showAlgorithmSection$ = this.facade.showAlgorithmSection$;
  public readonly showCategoriesSection$ = this.facade.showCategoriesSection$;
  public readonly showCloudSubject$ = this.facade.showCloudSubject$;
  public readonly showGenerateFinding$ = this.facade.showGenerateFinding$;
  public readonly showMultipleCheckbox$ = this.facade.showMultipleCheckbox$;
  public readonly showAllowNewOptionsCheckbox$ = this.facade.showAllowNewOptionsCheckbox$;
  public readonly showMosaicViewCheckbox$ = this.facade.showMosaicViewCheckbox$;
  public readonly showCounterCheckbox$ = this.facade.showCounterCheckbox$;
  public readonly showMappingPlaceholder$ = this.facade.showMappingPlaceholder$;

  private readonly destroy$ = new Subject<void>();

  constructor(
    public readonly facade: PipelineStepEditorFacade,
    private readonly dialog: MatDialog,
    private readonly injector: Injector
  ) {}

  ngOnInit(): void {
    this.facade.loadRoles();
    // Open the category dialog when the openCategoryDialog$ signal is emitted
    this.facade.openCategoryDialog$.pipe(takeUntil(this.destroy$)).subscribe(() => this.openCategoryDialog());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public trackByTask(_: number, item: { task: StepTask }): StepTask {
    return item.task;
  }

  public onTaskClicked(task: StepTask): void {
    this.facade.selectTask(task);
  }

  public onCloudSubjectChanged(value: CloudSubject): void {
    this.facade.setCloudSubject(value as StepAction);
  }

  public onGenerateFindingChanged(value: GenerateFinding): void {
    this.facade.setGenerateFinding(value);
  }

  public onMultipleChanged(checked: boolean): void {
    this.facade.setMultiple(checked);
  }

  public onAllowNewOptionsChanged(checked: boolean): void {
    this.facade.setAllowNewOptions(checked);
  }

  public onMosaicViewChanged(checked: boolean): void {
    this.facade.setEnableMosaicView(checked);
  }

  public onCounterChanged(checked: boolean): void {
    this.facade.setCounter(checked);
  }

  public onAddCategory(): void {
    this.facade.beginEditingCategory(null);
  }

  public onEditCategory(item: CategoryListItem): void {
    this.facade.beginEditingCategory(item.uuid);
  }

  public onRemoveCategory(item: CategoryListItem): void {
    this.facade.removeCategory(item.uuid);
  }

  public onCategoriesReordered(items: CategoryListItem[]): void {
    this.facade.reorderCategories(items.map((i) => i.uuid));
  }

  public onSave(): void {
    this.facade.saveRequiresConfirmation$.pipe(take(1), takeUntil(this.destroy$)).subscribe((requiresConfirmation) => {
      if (!requiresConfirmation) {
        this.facade.save();
        return;
      }
      this.dialog
        .open(ConfirmationDialogComponent, {
          data: {
            title: 'pipeline_step_editor.manual_mode.save_warning.title',
            text: 'pipeline_step_editor.manual_mode.save_warning.text',
            acceptButtonText: 'pipeline_step_editor.manual_mode.save_warning.accept',
            cancelButtonText: 'button.cancel',
            warningDialog: true,
            defaultAction: 'cancel',
          },
          hasBackdrop: true,
        })
        .afterClosed()
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe((result) => {
          if (result === 'accept') this.facade.save();
        });
    });
  }

  public onCancel(): void {
    this.facade.hasChanges$.pipe(take(1), takeUntil(this.destroy$)).subscribe((hasChanges) => {
      if (!hasChanges) {
        this.facade.cancel();
        return;
      }
      this.confirmDiscard$()
        .pipe(takeUntil(this.destroy$))
        .subscribe((discard) => {
          if (discard) this.facade.cancel();
        });
    });
  }

  /**
   * Exit gate for navigation-driven dismissal (browser back while the hosting
   * dialog is open): clean sessions exit silently; dirty sessions ask for
   * confirmation and, when the user discards, run the real cancel flow so the
   * dialog closes through the editor's own `confirmed$` handoff.
   */
  public canDeactivate(): Observable<boolean> | boolean {
    return this.facade.hasChanges$.pipe(
      take(1),
      switchMap((hasChanges) => {
        if (!hasChanges) return of(true);
        return this.confirmDiscard$().pipe(
          map((discard) => {
            if (discard) this.facade.cancel();
            return discard;
          })
        );
      })
    );
  }

  private confirmDiscard$(): Observable<boolean> {
    return this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'pipeline_step_editor.warnings.cancel_unsaved.title',
          text: 'pipeline_step_editor.warnings.cancel_unsaved.text',
          acceptButtonText: 'button.discard_changes',
          cancelButtonText: 'pipeline_step_editor.button.keep_editing',
          warningDialog: true,
          defaultAction: 'cancel',
        },
        hasBackdrop: true,
      })
      .afterClosed()
      .pipe(
        take(1),
        map((result) => result === 'accept')
      );
  }

  public onManualMode(): void {
    this.facade.enterManualMode();
  }

  public onExitManualMode(): void {
    this.facade.exitManualMode();
  }

  public onManualParamsChange(event: { value: unknown | null; valid: boolean }): void {
    this.facade.setManualParams({
      value: event.valid ? (event.value as Record<string, unknown>) : null,
      valid: event.valid,
    });
  }

  private openCategoryDialog(): void {
    this.dialog
      .open<PipelineCategoryEditorDialogComponent, undefined, PipelineCategoryResult | undefined>(
        PipelineCategoryEditorDialogComponent,
        {
          ...EDITOR_DIALOG_MEDIUM,
          hasBackdrop: true,
          autoFocus: false,
          injector: this.injector,
        }
      )
      .afterClosed()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((result) => this.facade.completeEditingCategory(result ?? null));
  }
}
